import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeWhile, tap } from 'rxjs/operators';
import { currentUser } from '../../../../../core/auth';
import { AppState } from '../../../../../core/reducers';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import { Template, Form, FieldTemplateMap } from '../../models';
import { FormsTemplateService, FormService } from '../../services';
import { RequestFormTemplateMapPage } from '../../actions';
import { FormTemplateMapDatasource } from '../../data-sources';
import { selectFormTemplateMapActionLoading, selectFormTemplateMapError } from '../../selectors';
import { CreateFormTemplateMapComponent } from '../create-form-template-map/create-form-template-map.component';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-form-template-map-list',
  templateUrl: './form-template-map-list.component.html',
  styleUrls: ['./form-template-map-list.component.scss'],
})
export class FormTemplateMapListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  dataSource: FormTemplateMapDatasource;
  displayedCols = ['id', 'modelName', 'template_name', 'warning', 'actions'];
  searchBarCols = ['searchId', 'searchForm', 'searchTemplate', 'searchWarning', 'searchActions'];
  showSearchBar = false;
  private isSuperAdmin = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  getFormAutocomplete = this.formService.findForms.bind(this.formService, {});
  @ViewChild('searchFormAutocomplete') searchFormAutocomplete: GeneralAutocompleteComponent;
  getTemplateAutocomplete = (searchTerm: string) =>
    this.templateService.findTemplates(this.isSuperAdmin, searchTerm);
  @ViewChild('searchTemplateAutocomplete') searchTemplateAutocomplete: GeneralAutocompleteComponent;

  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private formService: FormService,
    private templateService: FormsTemplateService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.pipe(select(currentUser)).subscribe((user) => {
        this.isSuperAdmin = user?.userType === 'Super Admin';
      }),
    );

    this.dataSource = new FormTemplateMapDatasource(this.store, this.paginator);

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadFormTemplateMapList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadFormTemplateMapList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadFormTemplateMapList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  formatFormOption(form: Form): string {
    return form?.name || '';
  }

  formatTemplateOption(template: Template): string {
    return template?.name?.replace(/\.[^/.]+$/, '') || '';
  }

  getFilterConfig() {
    const filter: any = {};

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.formName = this.searchFormAutocomplete?.value?.name;
      filter.templateName = this.searchTemplateAutocomplete?.value?.name;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadFormTemplateMapList() {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestFormTemplateMapPage({ query }));
  }

  displayMessage(message: string, messageType: MessageType): void {
    this.store
      .pipe(
        select(selectFormTemplateMapActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectFormTemplateMapError), take(1)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
            setTimeout(() => this.loadFormTemplateMapList(), 500);
          });
        }),
      )
      .subscribe();
  }

  requireReset(map: FieldTemplateMap): boolean {
    return map.model_version !== (map.model as Form)?.version;
  }

  addMapping(event: Event): void {
    event.stopPropagation();

    const map: FieldTemplateMap = {
      id: 0,
      model_type: 'FORM',
      model: null,
      model_version: 0,
      template: null,
      document_category_id: 0,
      mapping: '',
      prev_mapping: '',
      require_reset: false,
    };

    this.updateMapping(null, map);
  }

  resetMapping(event: Event, map: FieldTemplateMap): void {
    event.stopPropagation();

    const newMap: FieldTemplateMap = {
      id: 0,
      model_type: 'FORM',
      model: map.model,
      model_version: 0,
      template: map.template,
      document_category_id: 0,
      mapping: '',
      prev_mapping: map.mapping,
      require_reset: true,
    };

    this.updateMapping(null, newMap);
  }

  updateMapping(event: Event, map: FieldTemplateMap): void {
    event?.stopPropagation();

    const message = this.translate.instant(
      `FORMS_TEMPLATES_MODULE.MAPPING.${
        map.id ? 'UPDATE_MESSAGE' : map.require_reset ? 'RESET_MESSAGE' : 'CREATE_MESSAGE'
      }`,
    );
    const messageType = map.id ? MessageType.Update : MessageType.Create;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';
    dialogConfig.data = map;

    const dialogRef = this.dialog.open(CreateFormTemplateMapComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(message, messageType);
      });
  }

  reloadFormTemplateMapList(): void {
    this.paginator.pageIndex = 0;

    this.loadFormTemplateMapList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchFormAutocomplete?.setValue(null);
    this.searchFormAutocomplete?.updateValueAndValidity();
    this.searchTemplateAutocomplete?.setValue(null);
    this.searchTemplateAutocomplete?.updateValueAndValidity();
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadFormTemplateMapList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadFormTemplateMapList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadFormTemplateMapList();
  }
}
